const CHECKOUT_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';
const SCRIPT_LOAD_TIMEOUT_MS = 20000;
const PRE_OPEN_SETTLE_MS = 200;

/** @type {Promise<void> | null} */
let scriptLoadPromise = null;

/** @type {{ close?: () => void } | null} */
let activeCheckoutInstance = null;

/** @type {Promise<unknown> | null} */
let checkoutOpenPromise = null;

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function closeActiveCheckoutInstance() {
  if (!activeCheckoutInstance) return;
  try {
    if (typeof activeCheckoutInstance.close === 'function') {
      activeCheckoutInstance.close();
    }
  } catch {
    /* ignore */
  }
  activeCheckoutInstance = null;
}

function resetRazorpayPageStyles() {
  if (typeof document === 'undefined') return;
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
  document.body.style.pointerEvents = '';
  document.body.classList.remove('razorpay-backdrop-open', 'rzp-modal-open');
}

/** Backdrop only — never remove Razorpay containers/iframes (breaks the next open). */
function clearStuckBackdropOnly() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('.razorpay-backdrop').forEach((el) => {
    try {
      el.remove();
    } catch {
      /* ignore */
    }
  });
  resetRazorpayPageStyles();
}

function isRazorpayModalOpen() {
  if (typeof document === 'undefined') return false;
  return Boolean(
    document.querySelector('.razorpay-container, #razorpay-checkout-v2-container'),
  );
}

/**
 * Release checkout lock after dismiss/failure without touching Razorpay iframes.
 */
export async function unlockRazorpayCheckout() {
  checkoutOpenPromise = null;
  closeActiveCheckoutInstance();
  await delay(100);
  clearStuckBackdropOnly();
}

/** @deprecated Alias for unlockRazorpayCheckout */
export async function resetRazorpayCheckoutForRetry() {
  await unlockRazorpayCheckout();
}

/**
 * @returns {Promise<void>}
 */
export function loadRazorpayCheckoutScript() {
  if (typeof window !== 'undefined' && window.Razorpay) {
    return Promise.resolve();
  }

  if (!scriptLoadPromise) {
    scriptLoadPromise = new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        scriptLoadPromise = null;
        reject(new Error('Timed out loading Razorpay Checkout. Check your network or ad blocker.'));
      }, SCRIPT_LOAD_TIMEOUT_MS);

      const finish = () => {
        window.clearTimeout(timer);
        if (window.Razorpay) {
          resolve();
        } else {
          scriptLoadPromise = null;
          reject(new Error('Razorpay Checkout loaded but is unavailable.'));
        }
      };

      const fail = (message) => {
        window.clearTimeout(timer);
        scriptLoadPromise = null;
        reject(new Error(message));
      };

      const existing = document.querySelector(`script[src="${CHECKOUT_SCRIPT_URL}"]`);
      if (existing) {
        if (window.Razorpay || existing.getAttribute('data-razorpay-ready') === 'true') {
          finish();
          return;
        }
        existing.addEventListener('load', finish, { once: true });
        existing.addEventListener(
          'error',
          () => fail('Failed to load Razorpay Checkout.'),
          { once: true },
        );
        return;
      }

      const script = document.createElement('script');
      script.src = CHECKOUT_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        script.setAttribute('data-razorpay-ready', 'true');
        finish();
      };
      script.onerror = () => fail('Failed to load Razorpay Checkout.');
      document.head.appendChild(script);
    });
  }

  return scriptLoadPromise;
}

export async function prepareRazorpayCheckoutSession() {
  await loadRazorpayCheckoutScript();
}

export async function cleanupRazorpayCheckoutDom() {
  await unlockRazorpayCheckout();
}

function schedulePostCloseCleanup() {
  if (typeof window === 'undefined') return;
  window.setTimeout(() => {
    if (checkoutOpenPromise) return;
    activeCheckoutInstance = null;
    clearStuckBackdropOnly();
  }, 400);
}

/**
 * @param {Record<string, unknown>} options
 * @returns {Promise<{ razorpay_payment_id: string, razorpay_order_id: string, razorpay_signature: string }>}
 */
export async function openRazorpayCheckout(options) {
  if (checkoutOpenPromise && !isRazorpayModalOpen()) {
    checkoutOpenPromise = null;
  }

  if (checkoutOpenPromise) {
    throw new Error('A payment window is already open.');
  }

  await loadRazorpayCheckoutScript();

  if (!window.Razorpay) {
    throw new Error('Razorpay Checkout is unavailable.');
  }

  await unlockRazorpayCheckout();
  await delay(PRE_OPEN_SETTLE_MS);

  const {
    handler: _ignoredHandler,
    modal: _ignoredModal,
    retry: _ignoredRetry,
    onCheckoutOpen: _ignoredOnOpen,
    ...checkoutOptions
  } = options;

  checkoutOpenPromise = new Promise((resolve, reject) => {
    let settled = false;

    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      checkoutOpenPromise = null;
      closeActiveCheckoutInstance();
      schedulePostCloseCleanup();
      fn(value);
    };

    const instance = new window.Razorpay({
      ...checkoutOptions,
      retry: {
        enabled: false,
        ...(options.retry && typeof options.retry === 'object' ? options.retry : {}),
      },
      handler: (response) => settle(resolve, response),
      modal: {
        escape: true,
        confirm_close: true,
        backdropclose: true,
        ...(options.modal && typeof options.modal === 'object' ? options.modal : {}),
        ondismiss: () => {
          if (
            options.modal &&
            typeof options.modal === 'object' &&
            typeof options.modal.ondismiss === 'function'
          ) {
            options.modal.ondismiss();
          }
          settle(reject, new Error('Payment was not completed.'));
        },
      },
    });

    activeCheckoutInstance = instance;

    instance.on('payment.failed', (response) => {
      try {
        instance.close();
      } catch {
        /* ignore */
      }
      const description =
        response?.error?.description ||
        response?.error?.reason ||
        'Payment failed. Please try again.';
      settle(reject, new Error(description));
    });

    try {
      instance.open();
      if (typeof options.onCheckoutOpen === 'function') {
        options.onCheckoutOpen();
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not open Razorpay Checkout.';
      settle(reject, new Error(message));
    }
  });

  return checkoutOpenPromise;
}
